import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiJson } from "@/data/api";

export interface SessionUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  hasRealDisplayName?: boolean;
  profilePictureUrl?: string | null;
}

export interface SessionResponse {
  authenticated: boolean;
  user?: SessionUser;
}

export interface OrganizationOption {
  organization_id: string;
  name: string;
  workspace: { id: string; slug: string; name: string } | null;
}

interface AuthErrorResponse {
  error?: { message?: string } | string;
}

interface MagicSendResponse extends AuthErrorResponse {
  ok: boolean;
  expires_in_seconds?: number;
}

interface MagicVerifyResponse extends AuthErrorResponse {
  ok: boolean;
  redirect_to?: string;
  requires_organization_selection?: boolean;
  organizations?: OrganizationOption[];
}

export const authKeys = {
  all: ["auth"] as const,
  session: () => [...authKeys.all, "me"] as const,
};

export function authSessionQueryOptions() {
  return queryOptions({
    queryKey: authKeys.session(),
    queryFn: getSession,
    staleTime: 15_000,
    retry: false,
  });
}

export async function getSession(): Promise<SessionResponse> {
  try {
    return await apiJson<SessionResponse>("/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return { authenticated: false };
    }
    throw error;
  }
}

export async function logout(): Promise<void> {
  await apiJson<void>("/auth/logout", { method: "POST" });
}

async function markLoggedOut(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries();
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== authKeys.all[0],
  });
  queryClient.setQueryData(authKeys.session(), { authenticated: false });
}

export function useSession() {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery(authSessionQueryOptions());
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      await markLoggedOut(queryClient);
    },
  });
  const user =
    sessionQuery.data?.authenticated && sessionQuery.data.user
      ? sessionQuery.data.user
      : null;

  return {
    status: sessionQuery.isPending
      ? "loading"
      : user
        ? "authenticated"
        : "anonymous",
    user,
    error:
      sessionQuery.error instanceof Error ? sessionQuery.error.message : null,
    refetch: async () => {
      await sessionQuery.refetch();
    },
    logout: async () => {
      try {
        await logoutMutation.mutateAsync();
      } catch {
        await markLoggedOut(queryClient);
      }
    },
  } as const;
}

export function sendMagicCode(
  input: {
    email: string;
    returnTo?: string | null;
  },
  apiBaseUrl?: string,
): Promise<MagicSendResponse> {
  return apiJson<MagicSendResponse>("/auth/magic/send", {
    apiBaseUrl,
    method: "POST",
    json: {
      email: input.email,
      ...(input.returnTo ? { return_to: input.returnTo } : {}),
    },
  });
}

export function verifyMagicCode(
  input: {
    email: string;
    code: string;
  },
  apiBaseUrl?: string,
): Promise<MagicVerifyResponse> {
  return apiJson<MagicVerifyResponse>("/auth/magic/verify", {
    apiBaseUrl,
    method: "POST",
    json: {
      email: input.email,
      code: input.code,
    },
  });
}

export function selectOrganization(
  organizationId: string,
  apiBaseUrl?: string,
): Promise<MagicVerifyResponse> {
  return apiJson<MagicVerifyResponse>("/auth/organization/select", {
    apiBaseUrl,
    method: "POST",
    json: { organization_id: organizationId },
  });
}
